var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Input } from '@appspotr/ui-kit';
import { faAngleDown, faAngleUp, faArrowUpRight, faAtom, faBars, faCaretDown, faCodeBranch, faCopy, faDatabase, faEllipsis, faEllipsisV, faGrid, faHouseChimney, faLink, faList, faMobileButton, faObjectExclude, faObjectGroup, faQuestion, faServer, faTimes, faTrashCan, } from '@fortawesome/pro-regular-svg-icons';
import { faArrowRightFromLine, faHouseChimney as faHome, } from '@fortawesome/pro-solid-svg-icons';
import { INIT_PORT_LIMIT, LOGIC_PATCHES, NAVIGATION_TYPES, PATCHES_WITH_REDIRECTION, PATCH_SECONDARY_TYPES, PATCH_TYPES, SUBMIT_KEY, VIEW_PATCHES, } from '../SbGlobal/constants';
import { Divider, Icon, Wrapper } from '../SbGlobal/styles';
import { cleanPortName, formatPatchType, isReadOnlyCanvas } from './helpers';
import { selectCustomLogics, selectDatasets, selectPermissions, } from '../../../store/structureBuilder/selectors';
import { selectState } from '../../../store/state/selectors';
import PatchIcon from './PatchIcon';
import { ActionsDropdownContainer, ActionsDropdownItem, CountText, EntrypointIconContainer, HazardIcon, InputPorts, NodeContainer, NodeTopBar, NodeWrapper, OutputPorts, PatchTitle, PatchType, PatchWrapper, Popover, PopoverButton, PopoverContent, PopoverExit, PopoverHeader, PopoverInput, PopoverInputLabel, PopoverTitle, PortContainer, PortName, PortWrapper, PortsViewStateContainer, TopBarControllers, TopBarIconContainer, TopBarInfoContainer, TopBarTitleColumn, } from './styles';
import { useSelector } from 'react-redux';
import { sbColors as colors } from 'components/UI-helpers/colors';
import { Position, Handle, useStore } from 'reactflow';
import { NewEventInput } from './NewEventInput';
import { Banner } from './Banner';
var getPopoverTitle = function (node) {
    var _a;
    return node.patch.type === PATCH_TYPES.LOGIC &&
        ((_a = node === null || node === void 0 ? void 0 : node.patch) === null || _a === void 0 ? void 0 : _a.logic) === PATCH_SECONDARY_TYPES.CUSTOM_LOGIC
        ? 'New custom logic'
        : node.patch.type === PATCH_TYPES.LIST
            ? 'New list view'
            : 'New view';
};
var renderNavIcon = function (patch) {
    switch (patch.navigation) {
        case NAVIGATION_TYPES.GRID:
            return faGrid;
        case NAVIGATION_TYPES.TAB:
            return faEllipsis;
        case NAVIGATION_TYPES.DROPDOWN:
            return faCaretDown;
        default:
            return faBars;
    }
};
var renderIcon = function (patch) {
    var _a;
    if (patch === void 0) { patch = {}; }
    if ((_a = patch === null || patch === void 0 ? void 0 : patch.settings) === null || _a === void 0 ? void 0 : _a.icon) {
        return patch.settings.icon;
    }
    switch (patch === null || patch === void 0 ? void 0 : patch.type) {
        case PATCH_TYPES.VIEW:
            return faMobileButton;
        case PATCH_TYPES.DATASET:
            return faDatabase;
        case PATCH_TYPES.NAVIGATION:
            return renderNavIcon(patch);
        case PATCH_TYPES.LOGIC:
            if (patch.logic === PATCH_SECONDARY_TYPES.INTEGRATION) {
                return faLink;
            }
            if (patch.logic === PATCH_SECONDARY_TYPES.CUSTOM_LOGIC) {
                return faObjectGroup;
            }
            return faCodeBranch;
        case PATCH_TYPES.LOGIC_GROUP:
            return faObjectGroup;
        case PATCH_TYPES.LIST:
            if (patch.list === PATCH_SECONDARY_TYPES.FROM_DATASET) {
                return faDatabase;
            }
            return faList;
        default:
            return faQuestion;
    }
};
var typeColorCodes = {
    view: colors.purple,
    list: colors.purple,
    navigation: colors.marine,
    logic: colors.turquoise,
    logic_group: colors.blue,
    custom_logic: colors.blue,
};
var PatchWidgetComponent = function (props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y;
    var node = useMemo(function () { return props.data; }, [props.data]);
    var isNodeSelected = useMemo(function () { return props.selected; }, [props.selected]);
    var entrypoint = node === null || node === void 0 ? void 0 : node.entrypoint;
    var datasets = useSelector(selectDatasets);
    var states = useSelector(selectState);
    var permissions = useSelector(selectPermissions);
    var customLogics = useSelector(selectCustomLogics);
    var isAddPatchModalOpen = false;
    var navigate = useNavigate();
    var pathname = useLocation().pathname;
    var isViewOnly = useMemo(function () { return isReadOnlyCanvas(permissions); }, [permissions]);
    var isCustomLogicPatch = useMemo(function () {
        var _a, _b;
        return ((_a = node.patch) === null || _a === void 0 ? void 0 : _a.type) === PATCH_TYPES.LOGIC &&
            ((_b = node === null || node === void 0 ? void 0 : node.patch) === null || _b === void 0 ? void 0 : _b.logic) === PATCH_SECONDARY_TYPES.CUSTOM_LOGIC;
    }, [node.patch]);
    var isMissingCustomLogicParams = useMemo(function () { var _a, _b; return isCustomLogicPatch && ((_b = (_a = node === null || node === void 0 ? void 0 : node.patch) === null || _a === void 0 ? void 0 : _a.settings) === null || _b === void 0 ? void 0 : _b.missing_params); }, [isCustomLogicPatch, (_b = (_a = node === null || node === void 0 ? void 0 : node.patch) === null || _a === void 0 ? void 0 : _a.settings) === null || _b === void 0 ? void 0 : _b.missing_params]);
    var isRedirectionPatch = useMemo(function () {
        var _a;
        return PATCHES_WITH_REDIRECTION.includes(node.patch.type) ||
            PATCHES_WITH_REDIRECTION.includes((_a = node === null || node === void 0 ? void 0 : node.patch) === null || _a === void 0 ? void 0 : _a.logic);
    }, [(_c = node.patch) === null || _c === void 0 ? void 0 : _c.logic, node.patch.type]);
    var isCustomLogicDeleted = useMemo(function () {
        var _a, _b;
        return (node === null || node === void 0 ? void 0 : node.patch) &&
            !((_b = (_a = node === null || node === void 0 ? void 0 : node.patch) === null || _a === void 0 ? void 0 : _a.settings) === null || _b === void 0 ? void 0 : _b.toSync) &&
            isCustomLogicPatch &&
            (customLogics === null || customLogics === void 0 ? void 0 : customLogics.length) &&
            !(customLogics === null || customLogics === void 0 ? void 0 : customLogics.find(function (customLogic) { var _a; return customLogic.id === ((_a = node === null || node === void 0 ? void 0 : node.patch) === null || _a === void 0 ? void 0 : _a.custom_logic); }));
    }, [customLogics, isCustomLogicPatch, node === null || node === void 0 ? void 0 : node.patch]);
    var _z = useState((_d = node === null || node === void 0 ? void 0 : node.patch) === null || _d === void 0 ? void 0 : _d.name), tempPatchName = _z[0], setTempPatchName = _z[1];
    var _0 = useState(false), isActionsMenuVisible = _0[0], setActionsMenuVisibility = _0[1];
    var _1 = useState(false), isPopOverVisible = _1[0], setPopOverVisible = _1[1];
    var _2 = useState(false), isPortTrayExpanded = _2[0], setPortTrayExpanded = _2[1];
    /**
     * This is for the node.patchType === "list" patches.
     * The fn define whether the list patch has a listSource or not.
     * If there is a list source, it returns it, if not it returns 'undefined'
     */
    var listSource = useMemo(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        if (((_a = node.patch) === null || _a === void 0 ? void 0 : _a.type) === PATCH_TYPES.LIST &&
            ((_c = (_b = node.patch) === null || _b === void 0 ? void 0 : _b.settings) === null || _c === void 0 ? void 0 : _c.listSource)) {
            var sourceName = '';
            if (((_f = (_e = (_d = node.patch) === null || _d === void 0 ? void 0 : _d.settings) === null || _e === void 0 ? void 0 : _e.listSource) === null || _f === void 0 ? void 0 : _f.type) === 'dataset') {
                sourceName = (_g = datasets.find(function (dataset) { var _a, _b; return (dataset === null || dataset === void 0 ? void 0 : dataset.id) === ((_b = (_a = node.patch) === null || _a === void 0 ? void 0 : _a.settings.listSource) === null || _b === void 0 ? void 0 : _b.value); })) === null || _g === void 0 ? void 0 : _g.name;
            }
            if (((_k = (_j = (_h = node.patch) === null || _h === void 0 ? void 0 : _h.settings) === null || _j === void 0 ? void 0 : _j.listSource) === null || _k === void 0 ? void 0 : _k.type) === 'list') {
                sourceName = (_l = states.states.find(function (state) { var _a, _b; return "state:".concat(state === null || state === void 0 ? void 0 : state.key) === ((_b = (_a = node.patch) === null || _a === void 0 ? void 0 : _a.settings.listSource) === null || _b === void 0 ? void 0 : _b.value); })) === null || _l === void 0 ? void 0 : _l.label;
            }
            return __assign(__assign({}, node.patch.settings.listSource), { label: sourceName });
        }
        return undefined;
    }, [
        datasets,
        node.patch.settings.listSource,
        (_e = node.patch) === null || _e === void 0 ? void 0 : _e.type,
        states.states,
    ]);
    var inPorts = useMemo(function () { return node.patch.inputs; }, [node.patch.inputs]);
    var outPorts = useMemo(function () { return node.patch.outputs; }, [(_f = node === null || node === void 0 ? void 0 : node.patch) === null || _f === void 0 ? void 0 : _f.outputs]);
    var edges = useStore(function (s) { return s.edges; });
    /**
     * When output port tray is collapsed it should only show 2 ports if connected ports < 2, else all the connected ports.
     * This fn defines which ports to be  visible and which ports to be hidden
     */
    var modifiedOutPorts = useMemo(function () {
        var _a, _b, _c, _d, _e;
        var outPorts = ((_a = node === null || node === void 0 ? void 0 : node.patch) === null || _a === void 0 ? void 0 : _a.outputs.filter(function (op) { return op.type === 'event' || op.type === 'object'; })) || [];
        // Filter based on patchType and layout
        if ((node === null || node === void 0 ? void 0 : node.patch.type) === 'list') {
            var listFilters_1 = ['out:__onDrag__', 'out:__onDragStart__'];
            if (((_c = (_b = node === null || node === void 0 ? void 0 : node.patch) === null || _b === void 0 ? void 0 : _b.settings) === null || _c === void 0 ? void 0 : _c.layout) === 'map') {
                listFilters_1.push('out:__onRefresh__', 'out:__onEndReached__');
            }
            else {
                listFilters_1.push('out:__onDragEnd__');
            }
            outPorts = outPorts.filter(function (port) { return !listFilters_1.includes(port === null || port === void 0 ? void 0 : port.name); });
        }
        if (!outPorts.length)
            return [];
        // Early return for certain patch types or expanded tray
        if ((!__spreadArray(__spreadArray([], VIEW_PATCHES, true), [PATCH_TYPES.LOGIC_GROUP], false).includes(node.patch.type) &&
            ((_d = node === null || node === void 0 ? void 0 : node.patch) === null || _d === void 0 ? void 0 : _d.logic) !== PATCH_SECONDARY_TYPES.CUSTOM_LOGIC &&
            ((_e = node === null || node === void 0 ? void 0 : node.patch) === null || _e === void 0 ? void 0 : _e.logic) !== PATCH_SECONDARY_TYPES.ASYNC_LOGIC) ||
            isPortTrayExpanded) {
            return outPorts;
        }
        var selectedOutPorts = [];
        var isConnected = function (rawOutPort) {
            return edges.some(function (edge) {
                return edge.source === node.patch.id &&
                    edge.sourceHandle ===
                        (rawOutPort.name.includes('out:')
                            ? rawOutPort.name
                            : "out:".concat(rawOutPort.name));
            });
        };
        // First, pick all connected ports
        outPorts.forEach(function (port) {
            if (isConnected(port))
                selectedOutPorts.push(port);
        });
        // Fill up to INIT_PORT_LIMIT with remaining ports
        outPorts.forEach(function (port) {
            if (selectedOutPorts.length >= INIT_PORT_LIMIT)
                return;
            if (!selectedOutPorts.includes(port)) {
                selectedOutPorts.push(port);
            }
        });
        return selectedOutPorts;
    }, [
        edges,
        isPortTrayExpanded,
        node.patch.id,
        (_g = node.patch) === null || _g === void 0 ? void 0 : _g.logic,
        (_h = node.patch) === null || _h === void 0 ? void 0 : _h.outputs,
        (_k = (_j = node.patch) === null || _j === void 0 ? void 0 : _j.settings) === null || _k === void 0 ? void 0 : _k.layout,
        node.patch.type,
    ]);
    var actionMenuOptions = useMemo(function () {
        var _a, _b;
        return (_b = __spreadArray(__spreadArray([
            {
                name: 'Duplicate',
                icon: faCopy,
                color: colors.black,
                callback: function () { return node.onDuplicate(node.patch); },
            }
        ], (!LOGIC_PATCHES.includes(node.patch.type) && !entrypoint
            ? [
                {
                    name: 'Set as Starting Patch',
                    icon: faHouseChimney,
                    color: colors.black,
                    callback: node.onChangeEntryPoint,
                },
            ]
            : []), true), (!entrypoint && ((_a = node.patch) === null || _a === void 0 ? void 0 : _a.deletable)
            ? [
                {
                    name: 'Delete',
                    icon: faTrashCan,
                    color: colors.errorRed,
                    callback: node.onDelete,
                },
            ]
            : []), true)) === null || _b === void 0 ? void 0 : _b.filter(function (option) { return option && (option === null || option === void 0 ? void 0 : option.name) && (option === null || option === void 0 ? void 0 : option.icon); });
    }, [entrypoint, node]);
    var togglePortTray = function () {
        setPortTrayExpanded(!isPortTrayExpanded);
    };
    var toggleActionMenu = function (event) {
        event.preventDefault();
        setActionsMenuVisibility(!isActionsMenuVisible);
    };
    var closeActionMenu = function () {
        setActionsMenuVisibility(false);
    };
    var closePopover = function () {
        setPopOverVisible(false);
    };
    var isEventNameAvailable = function (value) {
        var _a;
        return (!((_a = outPorts.map(function (port) { return port.name; })) === null || _a === void 0 ? void 0 : _a.includes("out:".concat(value))) &&
            "out:__".concat(value, "__") !== 'out:__onPatchLoad__');
    };
    /**
     * This fn is keep reporting the position changes of a node, to its ports.
     * This is an override of the initial behaviour of reportPosition, since SBv2 goes with custom models.
     */
    // const reportPortPositionChange = useCallback(
    //   (ports) => {
    //     ports.forEach((port) => {
    //       port.updateCoords(engine.getPortCoords(port));
    //     });
    //   },
    //   [engine],
    // );
    var handleOnSubmitNewEvent = function (value) {
        var _a;
        if (isEventNameAvailable(value)) {
            node.onAddPort({
                name: value,
                viewId: (_a = node.patch) === null || _a === void 0 ? void 0 : _a.view,
            });
        }
    };
    var handleOnChangePopoverInput = function (e) {
        var _a, _b;
        setTempPatchName((_b = (_a = e.target) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '');
    };
    var handleOnEscapePopover = function () {
        var _a;
        handleUpdatePatch((_a = node === null || node === void 0 ? void 0 : node.patch) === null || _a === void 0 ? void 0 : _a.name);
    };
    var handleOnSubmitPopover = function () {
        handleUpdatePatch(tempPatchName);
    };
    var handleUpdatePatch = function (name) {
        var _a;
        if (!name)
            return;
        node.onRenamePatch(node.patch, name, {
            name: name,
            view_name: name,
            settings: __assign(__assign({}, (_a = node.patch) === null || _a === void 0 ? void 0 : _a.settings), { toSync: false }),
        }, isCustomLogicPatch);
        closePopover();
    };
    var handleOnClickAction = useCallback(function (option) {
        option.callback([node]);
        closeActionMenu();
    }, [node]);
    var handleRedirection = function (type, node) {
        switch (type) {
            case PATCH_SECONDARY_TYPES.CUSTOM_LOGIC:
                navigate(pathname.replace(/\/[^/]*$/, "/logic/".concat(node.patch.custom_logic)));
                break;
            case PATCH_TYPES.VIEW:
            case PATCH_TYPES.LIST:
                navigate(pathname.replace(/\/[^/]*$/, "/views/".concat(node.patch.view)));
                break;
            case PATCH_TYPES.LOGIC_GROUP:
                node.onOpenGroup(node.patch.id);
                break;
            default:
                break;
        }
    };
    var renderActionMenuOptions = useCallback(function () {
        return actionMenuOptions.map(function (option, index) { return (_jsxs(ActionsDropdownItem, __assign({ color: option.color, isLast: index === actionMenuOptions.length - 1, onClick: function (event) {
                event.preventDefault();
                event.stopPropagation();
                handleOnClickAction(option);
            } }, { children: [_jsx(TopBarIconContainer, __assign({ spaceRight: true, link: true }, { children: _jsx(PatchIcon, { icon: option.icon }) })), option.name] }), option.name)); });
    }, [actionMenuOptions, handleOnClickAction]);
    // useEffect(() => {
    //   if (VIEW_PATCHES?.includes(node.patchType)) {
    //     reportPortPositionChange(modifiedOutPorts);
    //   }
    // }, [modifiedOutPorts, node.patchType, reportPortPositionChange]);
    useEffect(function () {
        var _a, _b;
        /**
         * When a new patch is being created along with a new view, a new custom logic the 'toSync' prop will be true.
         * If 'toSync' is true and the patch type is either 'view', 'list' or 'custom_logic', the patch rename popover should open
         */
        if ((VIEW_PATCHES.includes(node.patch.type) ||
            isCustomLogicPatch ||
            node.patch.logic === 'output') &&
            ((_b = (_a = node === null || node === void 0 ? void 0 : node.patch) === null || _a === void 0 ? void 0 : _a.settings) === null || _b === void 0 ? void 0 : _b.toSync)) {
            setPopOverVisible(true);
        }
        else {
            setPopOverVisible(false);
        }
    }, [isCustomLogicPatch, listSource === null || listSource === void 0 ? void 0 : listSource.label, node]);
    useEffect(function () {
        if (!isNodeSelected) {
            closeActionMenu();
        }
    }, [isNodeSelected]);
    // useEffect(() => {
    //   /**
    //    * This effect is responsible to clean up the 'block patch delete' instruct
    //    */
    //   if (isBlur && !isNodeSelected && node.isLocked()) {
    //     node.setLocked(false);
    //     setBlur(false);
    //   }
    // }, [isBlur, isNodeSelected, node]);
    /**
     * If popover is visible, patch should blur
     * Or when node is selected and add patch modal is open, patch should blur
     * Otherwise, patch delete action will trigger
     */
    // useEffect(() => {
    //   if (
    //     (isPopOverVisible || (isAddPatchModalOpen && isNodeSelected)) &&
    //     !isBlur
    //   ) {
    //     node.setLocked(true);
    //     setBlur(true);
    //   }
    // }, [isPopOverVisible, isBlur, node, isAddPatchModalOpen, isNodeSelected]);
    // useLayoutEffect(() => {
    //   /**
    //    * This effect is responsible for bringing the selected patch to the front
    //    */
    //   const nodeWrapper = getNodeElementByNodeId(node.getID(), 'node');
    //   if (isNodeSelected && nodeWrapper) {
    //     nodeWrapper.style.zIndex = 99;
    //   }
    //   if (!isNodeSelected && nodeWrapper) {
    //     nodeWrapper.style.zIndex = 0;
    //   }
    // }, [isNodeSelected, node]);
    // const outgoingCount = useMemo(() => {
    //   return edges.filter(
    //     e => e.from_patch === node.id && e.from_arg === 'myHandle',
    //   ).length;
    // }, [edges, node.id]);
    return (_jsxs(NodeWrapper, { children: [isPopOverVisible && (_jsx(Popover, __assign({ top: -44 }, { children: _jsxs(Wrapper, { children: [_jsxs(PopoverHeader, { children: [_jsx(PopoverTitle, { children: getPopoverTitle(node) }), _jsx(PopoverExit, __assign({ onClick: handleOnEscapePopover }, { children: _jsx(Icon, { icon: faTimes, color: colors.default }) }))] }), _jsx(Divider, {}), _jsxs(PopoverContent, { children: [_jsxs(Wrapper, { children: [_jsx(PopoverInputLabel, { children: isCustomLogicPatch
                                                ? 'Custom logic name'
                                                : node.patch.type === PATCH_TYPES.LIST
                                                    ? 'List view name'
                                                    : 'View name' }), _jsx(Input, { autoFocus: true, style: PopoverInput, onChange: handleOnChangePopoverInput, onKeyDown: function (e) {
                                                if (e.key === SUBMIT_KEY) {
                                                    handleOnSubmitPopover();
                                                }
                                            }, value: tempPatchName })] }), _jsx(Button, __assign({ fill: true, style: PopoverButton, onClick: handleOnSubmitPopover, disabled: !tempPatchName }, { children: "Continue" }))] })] }) }))), _jsxs(NodeContainer, __assign({ color: typeColorCodes[node.patch.type] || colors.default, selected: isNodeSelected }, { children: [entrypoint && (_jsx(EntrypointIconContainer, { children: _jsx(Icon, { iconSize: 32, color: colors.greyActive, icon: node.patch.type === PATCH_TYPES.LOGIC
                                ? faArrowRightFromLine
                                : faHome }) })), PATCH_TYPES.LIST === node.patch.type &&
                        !((_m = (_l = node.patch) === null || _l === void 0 ? void 0 : _l.settings) === null || _m === void 0 ? void 0 : _m.toSync) &&
                        !(listSource === null || listSource === void 0 ? void 0 : listSource.label) ? (_jsx(HazardIcon, { type: "warning" })) : (_jsx(_Fragment, {})), isCustomLogicDeleted ? _jsx(HazardIcon, { type: "error" }) : _jsx(_Fragment, {}), !isCustomLogicDeleted ? (isMissingCustomLogicParams && _jsx(HazardIcon, { type: "warning" })) : (_jsx(_Fragment, {})), _jsxs(NodeTopBar, { children: [_jsx(ActionsDropdownContainer, __assign({ role: "menu", visible: isActionsMenuVisible, onMouseLeave: closeActionMenu }, { children: renderActionMenuOptions() })), _jsx(TopBarIconContainer, { children: _jsx(PatchIcon, { icon: renderIcon(node.patch) }) }), _jsxs(TopBarInfoContainer, { children: [_jsxs(TopBarTitleColumn, { children: [_jsx(PatchTitle, { children: node.patch.name }), node.patch.type !== PATCH_TYPES.LOGIC_GROUP &&
                                                ((_o = node === null || node === void 0 ? void 0 : node.patch) === null || _o === void 0 ? void 0 : _o.logic) !== PATCH_SECONDARY_TYPES.CUSTOM_LOGIC && (_jsx(PatchType, { children: node.patch.type === PATCH_TYPES.LOGIC
                                                    ? formatPatchType((_p = node.patch) === null || _p === void 0 ? void 0 : _p.logic)
                                                    : node.patch.type === PATCH_TYPES.NAVIGATION
                                                        ? formatPatchType((_q = node.patch) === null || _q === void 0 ? void 0 : _q.navigation)
                                                        : VIEW_PATCHES.includes(node.patch.type)
                                                            ? formatPatchType((_s = (_r = node.patch) === null || _r === void 0 ? void 0 : _r.view_name) !== null && _s !== void 0 ? _s : node.patch.type)
                                                            : formatPatchType(node.patch.type) }))] }), ((_t = node.patch) === null || _t === void 0 ? void 0 : _t.is_custom_logic) && (_jsx(TopBarIconContainer, __assign({ spaceRight: (_u = node.patch) === null || _u === void 0 ? void 0 : _u.is_custom_logic }, { children: _jsxs(PatchWrapper, { children: [_jsx(PatchIcon, { icon: faObjectExclude }), _jsx(CountText, { children: (_v = node.patch) === null || _v === void 0 ? void 0 : _v.custom_logic_count })] }) }))), _jsxs(TopBarControllers, { children: [!isCustomLogicDeleted && isRedirectionPatch && (_jsx(TopBarIconContainer, __assign({ link: true, onClick: function () {
                                                    var patchType = isCustomLogicPatch
                                                        ? PATCH_SECONDARY_TYPES.CUSTOM_LOGIC
                                                        : node.patch.type;
                                                    handleRedirection(patchType, node);
                                                } }, { children: _jsx(Icon, { icon: faArrowUpRight, iconSize: 24 }) }))), !isViewOnly && ((_w = node.patch) === null || _w === void 0 ? void 0 : _w.logic) !== 'input' && (_jsx(TopBarIconContainer, __assign({ link: true, onClick: toggleActionMenu }, { children: _jsx(Icon, { icon: faEllipsisV, iconSize: 24 }) })))] })] })] }), _jsx(InputPorts, { children: inPorts.map(function (port) {
                            return (_jsx(PortContainer, { children: _jsx(Handle, { type: "target", position: Position.Left, id: port.name, style: {
                                        marginTop: 20,
                                        marginLeft: 4,
                                        width: 15,
                                        height: 80,
                                        borderRadius: '999px',
                                        border: "2px solid ".concat(typeColorCodes[node.patch.type] || colors.default),
                                        backgroundColor: colors.white,
                                    } }) }, port.name));
                        }) }), node.patch.type === PATCH_TYPES.LIST ? (_jsx(Banner, { type: (listSource === null || listSource === void 0 ? void 0 : listSource.label) ? 'default' : 'warning', icon: (listSource === null || listSource === void 0 ? void 0 : listSource.label) && (listSource === null || listSource === void 0 ? void 0 : listSource.type)
                            ? (listSource === null || listSource === void 0 ? void 0 : listSource.type) === 'dataset'
                                ? faServer
                                : faAtom
                            : undefined, description: (listSource === null || listSource === void 0 ? void 0 : listSource.label) ? listSource === null || listSource === void 0 ? void 0 : listSource.label : 'Choose a list source' })) : (_jsx(_Fragment, {})), !isCustomLogicDeleted && isMissingCustomLogicParams ? (_jsx(Banner, { type: "warning", description: "Required inputs cannot be empty" })) : (_jsx(_Fragment, {})), isCustomLogicDeleted ? (_jsx(Banner, { type: "error", description: "Custom logic deleted" })) : (_jsx(_Fragment, {})), (modifiedOutPorts === null || modifiedOutPorts === void 0 ? void 0 : modifiedOutPorts.length) ? (_jsxs(OutputPorts, { children: [modifiedOutPorts === null || modifiedOutPorts === void 0 ? void 0 : modifiedOutPorts.map(function (port, index) {
                                var handleId = port.name.includes('out:')
                                    ? port.name
                                    : "out:".concat(port.name);
                                var outgoingCount = 0;
                                if (node.patch.type === 'navigation') {
                                    outgoingCount = 0;
                                }
                                else {
                                    outgoingCount = edges.filter(function (e) {
                                        return e.source === node.patch.id && e.sourceHandle === handleId;
                                    }).length;
                                }
                                return (_jsx(PortWrapper, __assign({ hidden: false }, { children: _jsxs(PortContainer, __assign({ outPorts: true }, { children: [_jsx(PortName, __assign({ style: { paddingRight: 10 } }, { children: cleanPortName(node.patch.type === 'logic_group'
                                                    ? port.group_name
                                                    : port.name) })), _jsx(Handle, { type: "source", position: Position.Right, id: port.name.includes('out:')
                                                    ? port.name
                                                    : "out:".concat(port.name), style: {
                                                    marginLeft: -4,
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '999px',
                                                    border: "2px solid ".concat(typeColorCodes[node.patch.type] || colors.default),
                                                    backgroundColor: colors.white,
                                                }, isConnectable: outgoingCount < 1 })] })) }), "".concat(port.name, "-").concat(index)));
                            }), _jsx("hr", {})] })) : (_jsx(_Fragment, {})), (__spreadArray(__spreadArray([], VIEW_PATCHES, true), [PATCH_TYPES.LOGIC_GROUP], false).includes(node.patch.type) ||
                        ((_x = node === null || node === void 0 ? void 0 : node.patch) === null || _x === void 0 ? void 0 : _x.logic) === PATCH_SECONDARY_TYPES.CUSTOM_LOGIC ||
                        ((_y = node === null || node === void 0 ? void 0 : node.patch) === null || _y === void 0 ? void 0 : _y.logic) === PATCH_SECONDARY_TYPES.ASYNC_LOGIC) &&
                        outPorts.length > INIT_PORT_LIMIT &&
                        !outPorts.every(function (port) {
                            return edges.find(function (edge) {
                                return edge.source === node.patch.id &&
                                    edge.sourceHandle ===
                                        (port.name.includes('out:') ? port.name : "out:".concat(port.name));
                            });
                        }) && (_jsx(PortsViewStateContainer, { children: _jsx(Icon, { iconSize: 18, className: "cursor-pointer", icon: isPortTrayExpanded ? faAngleUp : faAngleDown, onClick: togglePortTray }) })), !isViewOnly && VIEW_PATCHES.includes(node.patch.type) && (_jsx(NewEventInput, { node: node, onSubmit: handleOnSubmitNewEvent }))] }))] }));
};
export var PatchWidget = React.memo(PatchWidgetComponent);
